import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { CampaignDto } from '../../../../common/general-types/campaigns.dto';
import {
  BRIEF_BASICS_BRAND_CONTEXT,
  BRIEF_BASICS_CAMPAIGN_API,
  BRIEF_BASICS_CAMPAIGN_HOST,
} from './brief-basics-campaign.tokens';

/** Campaign load / select / create-modal state for the brief basics step. */
@Injectable()
export class BriefBasicsCampaignStore implements OnDestroy {
  private readonly campaignApi = inject(BRIEF_BASICS_CAMPAIGN_API);
  private readonly brandContext = inject(BRIEF_BASICS_BRAND_CONTEXT);
  private readonly host = inject(BRIEF_BASICS_CAMPAIGN_HOST);

  readonly allCampaigns = signal<CampaignDto[] | undefined>(undefined);
  readonly showCreateCampaignModal = signal(false);

  /** Sentinel for `el-select` when no campaign; must match the No Campaign `el-option` value. */
  readonly campaignSelectNoneValue = '__none__';

  readonly campaignSelectValue = computed(() => {
    const selected = this.host.selectedCampaign();
    return selected?.id ?? this.campaignSelectNoneValue;
  });

  private readonly subscriptionList = new Subscription();

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  loadCampaigns(): void {
    this.subscriptionList.add(
      this.campaignApi.getAllBusinessCampaigns(this.brandContext.focussedBrand.id).subscribe({
        next: (response) => {
          this.allCampaigns.set(response);
        },
      }),
    );
  }

  clearCampaign(): void {
    this.host.selectedCampaign.set(undefined);
  }

  selectCampaign(campaign: CampaignDto): void {
    this.host.selectedCampaign.set(campaign);
  }

  onAddCampaignClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showCreateCampaignModal.set(true);
  }

  onCampaignCreated(campaign: CampaignDto): void {
    this.showCreateCampaignModal.set(false);
    this.subscriptionList.add(
      this.campaignApi.getAllBusinessCampaigns(this.brandContext.focussedBrand.id).subscribe({
        next: (list) => {
          this.allCampaigns.set(list);
          const match = list.find((c) => c.id === campaign.id);
          this.host.selectedCampaign.set(match ?? campaign);
        },
      }),
    );
  }
}
